import React, { useState, useEffect } from 'react';
import { api } from '../api';

const ExternalPaymentsTab = ({ user, hoaData }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, completed, pending, failed, refunded
  const [externalPaymentsEnabled, setExternalPaymentsEnabled] = useState(false);
  const [hoaTimezone, setHoaTimezone] = useState('America/Chicago'); // Default timezone

  useEffect(() => {
    loadPayments();
    loadSettings();
  }, [filter]);

  const loadSettings = async () => {
    try {
      const { data } = await api.get(`/api/public-profile/${user.hoa_id}`);
      setExternalPaymentsEnabled(data.external_payments_enabled || false);
      // Get HOA timezone
      if (data.timezone) {
        setHoaTimezone(data.timezone);
      } else if (hoaData?.timezone) {
        setHoaTimezone(hoaData.timezone);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const { data } = await api.get(`/api/external-payments/list/${user.hoa_id}${statusParam}`);
      setPayments(data.payments || []);
    } catch (error) {
      console.error('Error loading external payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'bg-green-100 text-green-800',
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-purple-100 text-purple-800',
      canceled: 'bg-gray-100 text-gray-800',
      expired: 'bg-gray-100 text-gray-600'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusDisplay = (status) => {
    const displays = {
      completed: '✅ Completed',
      paid: '✅ Paid',
      pending: '⏳ Pending',
      processing: '🔄 Processing',
      failed: '❌ Failed',
      refunded: '↩️ Refunded',
      canceled: '🚫 Canceled',
      expired: '⏰ Expired'
    };
    return displays[status] || status;
  };

  const syncWithStripe = async () => {
    try {
      setSyncing(true);
      await api.post(`/api/external-payments/sync/${user.hoa_id}`);
      await loadPayments();
      alert('Payment statuses synced with Stripe successfully!');
    } catch (error) {
      console.error('Error syncing with Stripe:', error);
      alert('Failed to sync with Stripe. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Parse the date string - handle both ISO and non-ISO formats
    let date;
    if (typeof dateString === 'string') {
      // Ensure we have a proper ISO string
      if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
        // Missing timezone indicator - assume UTC
        date = new Date(dateString + 'Z');
      } else {
        date = new Date(dateString);
      }
    } else if (dateString instanceof Date) {
      date = dateString;
    } else {
      return 'Invalid Date';
    }
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Invalid Date';
    }
    
    // Convert to HOA's timezone
    try {
      return date.toLocaleString('en-US', {
        timeZone: hoaTimezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      console.error('Timezone conversion error:', e, 'timezone:', hoaTimezone);
      // Fallback if timezone is invalid
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading payments...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">External Payments Tracking</h2>
        <p className="text-gray-600 mt-1">Payments made through your public page by visitors (no login required)</p>
      </div>

      {/* Status Display Only - Toggle is in Public Page Settings */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">External Payments Status</h3>
            <p className="text-sm text-gray-600 mt-1">
              Current status of external payments feature. To enable/disable, go to <strong>Public Page Settings</strong> tab.
            </p>
          </div>
          <div className="ml-4">
            {externalPaymentsEnabled ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                ✅ Enabled
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                ⚠️ Disabled
              </span>
            )}
          </div>
        </div>
        {externalPaymentsEnabled && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              ✅ External payments are <strong>enabled</strong>. Visitors can make payments on your public HOA page.
            </p>
          </div>
        )}
        {!externalPaymentsEnabled && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              ⚠️ External payments are <strong>disabled</strong>. Go to <strong>Public Page Settings</strong> tab and enable "Accept External Payments" to allow public payments.
            </p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💡</span>
          <div className="flex-1">
            <h3 className="font-medium text-blue-900 mb-1">About External Payments</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Allows homeowners and visitors to pay without creating an account</li>
              <li>• Successful payments automatically create income transactions in your Budget</li>
              <li>• Receipts are sent to the payer's email automatically</li>
              <li>• Processing fees are calculated and displayed before payment</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium ${
            filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({payments.length})
        </button>
        <button
          onClick={() => setFilter('completed')}
          className={`px-4 py-2 rounded-lg font-medium ${
            filter === 'completed' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Completed
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-lg font-medium ${
            filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Pending
        </button>
        <button
          onClick={() => setFilter('refunded')}
          className={`px-4 py-2 rounded-lg font-medium ${
            filter === 'refunded' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Refunded
        </button>
        <button
          onClick={() => setFilter('failed')}
          className={`px-4 py-2 rounded-lg font-medium ${
            filter === 'failed' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Failed
        </button>
        
        {/* Sync with Stripe button */}
        <div className="ml-auto">
          <button
            onClick={syncWithStripe}
            disabled={syncing}
            className="px-4 py-2 rounded-lg font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {syncing ? (
              <>
                <span className="animate-spin">🔄</span>
                Syncing...
              </>
            ) : (
              <>
                🔄 Sync with Stripe
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 mt-1 text-right">Pull latest status from Stripe</p>
        </div>
      </div>

      {/* Payments Table */}
      {payments.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-6xl mb-4">📭</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Payments Yet</h3>
          <p className="text-gray-600">
            External payments will appear here once visitors make payments through your public page.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payment.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{payment.payer_name}</div>
                      <div className="text-xs text-gray-500">{payment.payer_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{payment.property_identifier}</div>
                      {payment.property_address && (
                        <div className="text-xs text-gray-500">{payment.property_address}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {payment.line_items.map((item, idx) => (
                          <div key={idx} className="mb-1">
                            <span className="font-medium">{item.type}:</span> {item.description} - {formatCurrency(item.amount)}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(payment.total_amount)}</div>
                      <div className="text-xs text-gray-500">
                        Subtotal: {formatCurrency(payment.subtotal)}<br />
                        Fee: {formatCurrency(payment.processing_fee)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.payment_method === 'card' ? '💳 Card' : '🏦 Bank'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(payment.status)}`}>
                        {getStatusDisplay(payment.status)}
                      </span>
                      {payment.stripe_status && payment.stripe_status !== payment.status && (
                        <div className="text-xs text-gray-500 mt-1">
                          Stripe: {payment.stripe_status}
                        </div>
                      )}
                      {payment.refunded_at && (
                        <div className="text-xs text-purple-600 mt-1">
                          Refunded: {formatDate(payment.refunded_at)}
                        </div>
                      )}
                      {payment.completed_at && !payment.refunded_at && (
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDate(payment.completed_at)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {payments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Payments</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{payments.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Amount</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {formatCurrency(payments.reduce((sum, p) => sum + (p.total_amount || 0), 0))}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Processing Fees</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">
              {formatCurrency(payments.reduce((sum, p) => sum + (p.processing_fee || 0), 0))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalPaymentsTab;
